import React, { useEffect, useRef, useState } from "react";
import InputAdornment from "@mui/material/InputAdornment";
import { IconButton, TextField } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { FaCheckCircle } from "react-icons/fa";
import { FaRegCheckCircle } from "react-icons/fa";
import eventEmitter from "../../eventEmitter";
import {
    package_mapping,
    cable_length_pricing_mapping,
} from "../../../../../constants";

const useStyles = makeStyles({
    root: {
        "& .MuiInputBase-root": {
            borderRadius: 10,
            "& .MuiInputBase-input": {
                font: "normal normal normal 16px / 25px Poppins",
                color: "#505050",
            },
            "& .MuiInputAdornment-root": {
                "& .MuiIconButton-root": {
                    background: "transparent",
                },
                "& .type-check": {
                    borderRadius: "50%",
                    fontSize: 27,
                },
                "& .type-checked": {
                    fill: "#2A7CD6",
                },
                "& .type-unchecked": {
                    background: "#EAEBEF 0% 0% no-repeat padding-box",
                    fill: "#DADADA",
                },
            },
            '& input[type="number"]::-webkit-outer-spin-button, & input[type="number"]::-webkit-inner-spin-button':
                {
                    "-webkit-appearance": "none",
                    margin: 0,
                },
            '& input[type="number"]': {
                "-moz-appearance": "textfield", // for Firefox
            },
        },
        "& .MuiFormLabel-root": {
            font: "normal normal normal 19px/21px Poppins",
            color: "#505050",
            "&.Mui-focused": {
                color: "#505050",
            },
        },
        "& .MuiTypography-root": {
            fontSize: 16,
        },
        "& .MuiOutlinedInput-notchedOutline": {
            border: "1px solid #B0B0B0",
            "& legend": {
                fontSize: "15.5px",
            },
        },
    },
});

function CurrencyInput({
    id,
    name,
    type,
    value,
    label,
    placeholder,
    onChange,
    isChecked,
    onTypeCheck,
    purchase_type,
    addTypeCheck,
    endAdornment,
    selectedType,
    errors,
    readOnly,
    softLimit = null,
    softLimitWarningMessage = "Manager approval required",
    displaySoftLimitErrorMsg = 'yes'
}) {
    const classes = useStyles();

    const InputRef = useRef(null);
    const [validation_errors, setErrors] = useState(errors);
    useEffect(() => {
        setErrors(errors);
    }, [errors]);
    const removeError = (key) => {
        // const { [key]: _, ...rest } = validation_errors;
        // setErrors(rest);
    };

    // This could be extracted out into its own function. But it's okay for now.
    const showError = (addTypeCheck && !selectedType) || validation_errors?.[name]
    const showSoftLimitWarning = softLimit !== null && softLimit !== undefined && 
      value !== "" && 
      parseFloat(value) <= parseFloat(softLimit) && !showError && displaySoftLimitErrorMsg === 'yes';;


    useEffect(() => {
        eventEmitter.on("onPackageChange", (data) => {
            if (
                data.id == id &&
                (name == "monthly_fee_default" ||
                    name == "purchase_for_default" ||
                    name == "lease_for_default" ||
                    name == "rent_for_default" ||
                    name == "saas_fee_default")
            ) {
                setTimeout(() => {
                    try {
                        let package_pricing = package_mapping[data.value];
                        let total_monthly_fee =
                            parseFloat(package_pricing) + parseFloat(value);
                        const nativeInputValueSetter =
                            Object.getOwnPropertyDescriptor(
                                window.HTMLInputElement.prototype,
                                "value",
                            ).set;
                        if (data.value) {
                            nativeInputValueSetter.call(
                                InputRef.current,
                                total_monthly_fee.toFixed(2),
                            );
                        } else {
                            nativeInputValueSetter.call(
                                InputRef.current,
                                value,
                            );
                        }
                        const event = new Event("input", { bubbles: true });
                        InputRef.current.dispatchEvent(event);
                    } catch (error) {}
                }, 500);
            }
        });

        eventEmitter.on("onCableLengthChange", (data) => {
            if (
                data.id == id &&
                (name == "monthly_fee_default" ||
                    name == "purchase_for_default" ||
                    name == "lease_for_default" ||
                    name == "rent_for_default" ||
                    name == "saas_fee_default")
            ) {
                setTimeout(() => {
                    try {
                        let package_pricing =
                            cable_length_pricing_mapping[data.value];
                        let total_monthly_fee =
                            parseFloat(package_pricing) + parseFloat(value);
                        const nativeInputValueSetter =
                            Object.getOwnPropertyDescriptor(
                                window.HTMLInputElement.prototype,
                                "value",
                            ).set;
                        if (data.value) {
                            nativeInputValueSetter.call(
                                InputRef.current,
                                total_monthly_fee.toFixed(2),
                            );
                        } else {
                            nativeInputValueSetter.call(
                                InputRef.current,
                                value,
                            );
                        }
                        const event = new Event("input", { bubbles: true });
                        InputRef.current.dispatchEvent(event);
                    } catch (error) {}
                }, 500);
            }
        });

        // Cleanup to prevent memory leaks
        return () => {
            eventEmitter.off("onPackageChange");
            eventEmitter.off("onCableLengthChange");
        };
    }, []);

    return (
        <TextField
            label={label}
            id="outlined-start-adornment"
            fullWidth
            className={classes.root}
            placeholder={placeholder}
            value={value}
            onChange={(e) => {
                if (e.target.value.length > 10) return;
                onChange(e);
                removeError(e.target.name);
            }}
            name={name}
            type={type}
            error={(addTypeCheck && !selectedType) || validation_errors?.[name]}
            helperText={showSoftLimitWarning ? softLimitWarningMessage : ""}
            FormHelperTextProps={{
                style: {
                    color: showSoftLimitWarning ? '#ff9800' : 'inherit',
                    fontSize: '10px',
                    marginLeft: '8px',
                }
            }}
            inputRef={InputRef}
            InputProps={{
                ...(typeof readOnly !== "undefined" && { readOnly }),
                startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                ),
                endAdornment: (endAdornment || addTypeCheck) && (
                    <InputAdornment position="end">
                        {endAdornment && (
                            <InputAdornment position="end">
                                {endAdornment}
                            </InputAdornment>
                        )}
                        {addTypeCheck && (
                            <IconButton
                                onClick={(e) => {
                                    if (!isChecked) {
                                        onTypeCheck(e, purchase_type);
                                    }
                                }}
                                // onMouseDown={handleMouseDownPassword}
                                edge="end"
                            >
                                {isChecked ? (
                                    <FaCheckCircle className="type-check type-checked" />
                                ) : (
                                    <FaRegCheckCircle className="type-check type-unchecked" />
                                )}
                            </IconButton>
                        )}
                    </InputAdornment>
                ),
            }}
        />
    );
}

export default CurrencyInput;
